import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";

// Campaign photo wheel — auto-spins, pauses on hover. Add slides via CAMPAIGN_PHOTOS in config.
export const PhotoCarousel = ({ photos = [], autoplayMs = 4000 }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "center", dragFree: false });
  const [selected, setSelected] = useState(0);
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const [playing, setPlaying] = useState(!reduceMotion);
  const timerRef = useRef(null);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // Autoplay loop
  useEffect(() => {
    if (!emblaApi || !playing) return;
    timerRef.current = setInterval(() => emblaApi.scrollNext(), autoplayMs);
    return () => clearInterval(timerRef.current);
  }, [emblaApi, playing, autoplayMs]);

  const pause = () => setPlaying(false);
  const resume = () => !reduceMotion && setPlaying(true);

  return (
    <div className="relative" data-testid="photo-carousel">
      <div
        className="overflow-hidden"
        ref={emblaRef}
        onMouseEnter={pause}
        onMouseLeave={resume}
      >
        <div className="flex">
          {photos.map((photo, i) => (
            <div className="min-w-0 flex-[0_0_90%] md:flex-[0_0_58%] pr-4 md:pr-6" key={i}>
              <figure>
                <div className="overflow-hidden rounded-sm border border-slate-200 bg-slate-100 shadow-sm">
                  <div className="aspect-[3/2] w-full">
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      loading="lazy"
                      style={{ imageOrientation: "from-image", objectPosition: photo.position || "center" }}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </figure>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mt-8">
        <button
          onClick={scrollPrev}
          aria-label="Previous photo"
          data-testid="carousel-prev"
          className="focus-ring h-12 w-12 border border-slate-300 flex items-center justify-center hover:bg-[hsl(var(--navy))] hover:text-white hover:border-transparent transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={scrollNext}
          aria-label="Next photo"
          data-testid="carousel-next"
          className="focus-ring h-12 w-12 border border-slate-300 flex items-center justify-center hover:bg-[hsl(var(--navy))] hover:text-white hover:border-transparent transition-colors"
        >
          <ChevronRight size={20} />
        </button>
        <button
          onClick={() => setPlaying((p) => !p)}
          aria-label={playing ? "Pause slideshow" : "Play slideshow"}
          data-testid="carousel-toggle-play"
          className="focus-ring h-12 w-12 border border-slate-300 flex items-center justify-center hover:bg-[hsl(var(--navy))] hover:text-white hover:border-transparent transition-colors"
        >
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </button>

        {/* Dots */}
        <div className="ml-2 flex items-center gap-2">
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              aria-label={`Go to photo ${i + 1}`}
              data-testid={`carousel-dot-${i}`}
              className={`h-2 rounded-full transition-all ${
                i === selected ? "w-8 bg-red-700" : "w-2 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>

        <span className="ml-auto font-mono-num text-sm text-slate-400">
          {String(selected + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}
        </span>
      </div>
    </div>
  );
};
